import React, {useState} from 'react';
import {useIntl} from 'react-intl';
import Styled from 'styled-components';
import {
	Button,
	ButtonDropdownField,
	DropdownList,
	EMPHASIS,
	FlexRow,
	Icon,
	SubHeading,
	Text,
	tokensForThemeProvider,
} from '@forecast-it/design-system';
import {DeliverableDraft} from './baselineTemplateDraft';

// The light blue bar the Baseline edit page uses for its Select Deliverable card.
const Bar = Styled(FlexRow)`
	background-color: ${tokensForThemeProvider.sky.ref.color.blue[200]};
	border-radius: ${tokensForThemeProvider.sky.sys.spacing[100]};
	min-height: 56px;
	padding: 0 8px;
`;

// The picker spans the bar: the template editor's column is narrower than the Baseline page, so half of it
// would leave the dropdown too narrow to read.
const PickerRow = Styled.div`
	width: 100%;
`;

// The dropdown keeps the width it has on the Baseline page and narrows only when the bar can't fit it. Its menu
// takes the same width, so deliverable names stay on one line.
const DropdownWidth = Styled.div`
	flex: 0 1 336px;
	min-width: 160px;
`;

// The separate "add new" row at the bottom of the dropdown, drawn like the Baseline's. Its top border divides
// it from the deliverables above, so it is drawn only when there are some: alone, it would divide nothing.
const AddNewRow = Styled.div<{$belowDeliverables: boolean}>`
	cursor: pointer;
	padding: 16px;
	border-top: ${props =>
		props.$belowDeliverables ? `1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative}` : 'none'};
`;

type Props = {
	cyPrefix: string;
	/** Select Deliverable on an empty phase, Select Another Deliverable once it has one. */
	label: string;
	/** Template deliverables not on this phase yet. */
	available: DeliverableDraft[];
	onSelect: (deliverableKey: string) => void;
	/** Opens the add-deliverable modal. */
	onAddNew: () => void;
};

export const TemplateSelectDeliverableCard = ({cyPrefix, label, available, onSelect, onAddNew}: Props): JSX.Element => {
	const {formatMessage} = useIntl();
	const [open, setOpen] = useState(false);
	const [selected, setSelected] = useState<DeliverableDraft | null>(null);
	const close = () => {
		setOpen(false);
		setSelected(null);
	};
	return (
		<Bar alignItems="center" data-cy={cyPrefix}>
			{open ? (
				<PickerRow>
					<FlexRow gap="s" alignItems="center">
						<Icon icon="deliverable" size="m" />
						<Text>{formatMessage({id: 'common.deliverable'})}</Text>
						<DropdownWidth>
							<DropdownList>
								<DropdownList.Trigger>
									<ButtonDropdownField data-cy={`${cyPrefix}-dropdown`}>
										{selected ? (
											<Text ellipsis size="1">
												{selected.name}
											</Text>
										) : (
											<Text ellipsis size="1" color="auxiliary">
												{formatMessage({id: 'deliverable.select_a_deliverable'})}
											</Text>
										)}
									</ButtonDropdownField>
								</DropdownList.Trigger>
								<DropdownList.Content offset={10} useTriggerWidth>
									{available.map(deliverable => (
										<DropdownList.Item key={deliverable.key} onClick={() => setSelected(deliverable)}>
											{deliverable.name}
										</DropdownList.Item>
									))}
									<AddNewRow
										$belowDeliverables={available.length > 0}
										onClick={onAddNew}
										data-cy={`${cyPrefix}-add-new`}
									>
										<FlexRow justifyContent="">
											<SubHeading>{formatMessage({id: 'deliverable.add_new_deliverable'})}</SubHeading>
											<Icon icon="plus" size="m" />
										</FlexRow>
									</AddNewRow>
								</DropdownList.Content>
							</DropdownList>
						</DropdownWidth>
						<Button
							emphasis={EMPHASIS.HIGH}
							disabled={!selected}
							onPress={() => {
								// The deliverable may have been taken or removed while it sat selected.
								if (selected && available.some(deliverable => deliverable.key === selected.key)) {
									onSelect(selected.key);
								}
								close();
							}}
							data-cy={`${cyPrefix}-confirm`}
						>
							{formatMessage({id: 'common.select'})}
						</Button>
						<Button emphasis={EMPHASIS.MEDIUM} onPress={close} data-cy={`${cyPrefix}-cancel`}>
							{formatMessage({id: 'common.cancel'})}
						</Button>
					</FlexRow>
				</PickerRow>
			) : (
				<Button emphasis={EMPHASIS.LOW} icon="deliverable" onPress={() => setOpen(true)} data-cy={`${cyPrefix}-open`}>
					{label}
				</Button>
			)}
		</Bar>
	);
};
